// Pure helpers shared across the Detail/ drawer components.
// Keep this file framework-free (no JSX, no React imports) so the helpers
// stay easy to unit-test and re-use from non-React call sites.

export type ActionPresentation = { label: string; preposition: "by" | "to" };

export type ActivityIconType = "check-circle" | "xmark-circle" | "circle";

// Picks the Activity timeline marker for a given action. Positive outcomes get
// a check, negative outcomes get an xmark, everything else (state changes,
// reassignments, unknown verbs) falls through to a neutral outlined circle.
// To classify a new BE verb, just add it to the matching set below.
const CHECK_CIRCLE_ACTIONS = new Set([
  "complete",
  "approve",
  "approve_on_behalf",
  "approved_on_behalf_of_client",
  "task_approved",
  "submit_for_review",
  "task_submitted",
  "start_and_submit",
  "external_documents_attached",
]);

const XMARK_CIRCLE_ACTIONS = new Set(["reject", "task_rejected", "return"]);

export function iconForAction(action: string): ActivityIconType {
  if (CHECK_CIRCLE_ACTIONS.has(action)) return "check-circle";
  if (XMARK_CIRCLE_ACTIONS.has(action)) return "xmark-circle";
  return "circle";
}

// Maps backend `action` codes (mirrors the verbs used in useTaskActions / FSM)
// to the user-facing phrase shown in the Activity timeline. Unknown actions
// fall through to a humanized label so a new BE verb still renders.
export function presentAction(action: string): ActionPresentation {
  switch (action) {
    case "complete":
      return { label: "Marked complete", preposition: "by" };
    case "skip":
      return { label: "Marked skipped", preposition: "by" };
    case "approve":
    case "approve_on_behalf":
    case "task_approved":
      return { label: "Approved", preposition: "by" };
    case "approved_on_behalf_of_client":
      return { label: "Approved on behalf of client", preposition: "by" };
    case "reject":
    case "task_rejected":
      return { label: "Rejected", preposition: "by" };
    case "return":
      return { label: "Returned", preposition: "by" };
    case "submit_for_review":
      return { label: "Submitted for review", preposition: "by" };
    case "task_submitted":
      return { label: "Submitted", preposition: "by" };
    case "start_and_submit":
      return { label: "Started and submitted", preposition: "by" };
    case "start_work":
    case "task_started":
      return { label: "Started", preposition: "by" };
    case "assign":
    case "assigned":
      return { label: "Assigned", preposition: "to" };
    case "task_self_assigned":
      return { label: "Self-assigned", preposition: "by" };
    case "task_reassigned":
      return { label: "Reassigned", preposition: "to" };
    case "task_handed_off":
      return { label: "Handed off", preposition: "to" };
    case "claim":
    case "task_checker_claimed":
      return { label: "Claimed", preposition: "by" };
    case "unassign":
    case "task_unassigned":
      return { label: "Unassigned", preposition: "by" };
    case "bts_note_added":
      return { label: "Comment added", preposition: "by" };
    case "external_documents_attached":
      return { label: "Documents shared for review", preposition: "by" };
    default: {
      const humanized = action.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return { label: humanized, preposition: "by" };
    }
  }
}

/**
 * Returns up-to-two-letter initials for an avatar pill.
 *   "Gloria Myers" → "GM"
 *   "Cher"         → "CH"
 *   ""             → "?"
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
